import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import PageShell from '../../../Components/PageShell';
import AdminNav from '../AdminNav';
import EmailsTabs from './EmailsTabs';
import { STATUS_META, campaignTotals, openRate } from './campaignMeta';
import './EmailsPage.css';

// Admin campaign list — mirrors OrgsPage's table idiom. Counts come off the
// campaign row's totals JSON (kept current by the send loop + webhook), so the
// list needs exactly one request.
const EmailsPage = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        api.get('admin/email/campaigns')
            .then((r) => setCampaigns(r.items || []))
            .catch((e) => setError(e.response?.data?.error || e.message))
            .finally(() => setLoading(false));
    }, []);

    const fmt = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric', month: 'short', year: 'numeric'
        });
    };

    return (
        <PageShell wrap={false}>
            <AdminNav />
            <div className="fs admin-emails-page">
                <EmailsTabs />
                <div className="admin-emails-header">
                    <h1 className="admin-emails-title">Email campaigns</h1>
                    <span className="admin-emails-count">{loading ? '' : `${campaigns.length} total`}</span>
                    <button
                        type="button"
                        className="btn btn-primary admin-emails-new-btn"
                        onClick={() => navigate('/admin/emails/new')}
                    >
                        New campaign
                    </button>
                </div>
                <p className="admin-emails-blurb">
                    One-off HTML emails to FreightSource users. Click a campaign to edit its
                    content and audience, prepare recipients, and send.
                </p>

                {error && (
                    <div className="admin-emails-error">Error loading campaigns: {error}</div>
                )}

                {loading ? (
                    <div className="admin-emails-loading">Loading…</div>
                ) : (
                    <div className="admin-emails-table-wrap">
                        <table className="admin-emails-table">
                            <thead>
                                <tr>
                                    <th>Campaign</th>
                                    <th>Status</th>
                                    <th className="num">Recipients</th>
                                    <th className="num">Sent</th>
                                    <th className="num">Delivered</th>
                                    <th className="num">Opened</th>
                                    <th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campaigns.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="admin-emails-empty">
                                            No campaigns yet — create one to get started.
                                        </td>
                                    </tr>
                                ) : (
                                    campaigns.map((c) => {
                                        const totals = campaignTotals(c);
                                        const meta = STATUS_META[c.status] || { label: c.status, badge: 'ink' };
                                        const rate = openRate(totals);
                                        return (
                                            <tr key={c.id} onClick={() => navigate(`/admin/emails/${c.id}`)}>
                                                <td>
                                                    <span className="admin-emails-name">{c.name}</span>
                                                    <div className="admin-emails-subject">{c.subject}</div>
                                                </td>
                                                <td><span className={`badge ${meta.badge}`}>{meta.label}</span></td>
                                                <td className="num">{totals.recipients}</td>
                                                <td className="num">{totals.sent}</td>
                                                <td className="num">{totals.delivered}</td>
                                                <td className="num">
                                                    {totals.opened}
                                                    {rate !== null && <span className="admin-emails-rate"> ({rate}%)</span>}
                                                </td>
                                                <td className="admin-emails-date">{fmt(c.created_at)}</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </PageShell>
    );
};

export default EmailsPage;
